import { useLogout } from "@refinedev/core";
import {
  Banknote,
  BanknoteArrowDown,
  Building2,
  ChartPie,
  ChevronRight,
  CreditCard,
  FileUp,
  Grid3x3,
  HandCoins,
  KeyRound,
  Landmark,
  List,
  LogOut,
  Menu,
  Moon,
  Receipt,
  ScrollText,
  Table2,
  Tags,
  Truck,
  Users,
  Wallet,
  Wand2,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router";

import { Annotator } from "@/components/Annotator";
import {
  CONTROL,
  has,
  KASSA,
  PAYROLL,
  PERSONAL_FINANCE,
  PERMISSIONS_EDIT,
  type PermissionCode,
  PNL,
  STATEMENTS,
  useIdentity,
} from "@/lib/access";

/**
 * Пункт меню. `to` ОБЯЗАТЕЛЕН: кнопка, за которой нет экрана, — обещание,
 * которого кабинет не исполняет (правило перенесено из supply).
 * `need` — право, без которого пункт не рисуется.
 */
type NavItem = { label: string; icon: ReactNode; to: string; need?: PermissionCode };

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: "Финансы",
    items: [
      { label: "P&L", icon: <Table2 size={14} />, to: "/pnl", need: PNL },
      { label: "Оплаты", icon: <HandCoins size={14} />, to: "/pnl/payments", need: PNL },
      { label: "Расходы", icon: <Wallet size={14} />, to: "/pnl/expenses", need: PNL },
      { label: "Операции", icon: <List size={14} />, to: "/pnl/operations", need: PNL },
      { label: "Контрагенты", icon: <Users size={14} />, to: "/pnl/counterparties", need: PNL },
      { label: "Счета", icon: <CreditCard size={14} />, to: "/pnl/accounts", need: PNL },
      { label: "Эквайринг", icon: <Receipt size={14} />, to: "/pnl/acquiring", need: PNL },
      { label: "Правила", icon: <Wand2 size={14} />, to: "/pnl/rules", need: PNL },
    ],
  },
  {
    // Зарплата: банковские реестры ↔ выплаты CRM по людям; разбивка ФОТ.
    group: "Зарплата",
    items: [
      { label: "Банковские реестры", icon: <ScrollText size={14} />, to: "/payroll/registries", need: PAYROLL },
      { label: "Разбивка ФОТ", icon: <ChartPie size={14} />, to: "/pnl/payroll", need: PNL },
    ],
  },
  {
    group: "Наличные",
    items: [{ label: "Кассы салонов", icon: <Wallet size={14} />, to: "/cash", need: KASSA }],
  },
  {
    group: "Кассы CRM",
    items: [
      { label: "Кассы", icon: <Banknote size={14} />, to: "/kassa", need: KASSA },
      { label: "Статьи CRM", icon: <Tags size={14} />, to: "/kassa/articles", need: KASSA },
    ],
  },
  {
    // Сверки учёта CRM с банком; каждая проверка — свой пункт.
    group: "Контроль",
    items: [
      { label: "Инкассация", icon: <BanknoteArrowDown size={14} />, to: "/control/collection", need: CONTROL },
      { label: "Доставка", icon: <Truck size={14} />, to: "/control/delivery", need: CONTROL },
    ],
  },
  {
    group: "Выписки",
    items: [
      { label: "Загрузка", icon: <FileUp size={14} />, to: "/statements", need: STATEMENTS },
      { label: "Покрытие", icon: <Grid3x3 size={14} />, to: "/statements/coverage", need: STATEMENTS },
      { label: "ИП", icon: <Building2 size={14} />, to: "/statements/entrepreneurs", need: STATEMENTS },
      { label: "Банки", icon: <Landmark size={14} />, to: "/statements/banks", need: STATEMENTS },
    ],
  },
  {
    group: "Личное",
    items: [{ label: "Личные финансы", icon: <Wallet size={14} />, to: "/personal", need: PERSONAL_FINANCE }],
  },
  {
    group: "Настройки",
    items: [{ label: "Полномочия", icon: <KeyRound size={14} />, to: "/settings/permissions", need: PERMISSIONS_EDIT }],
  },
];

/**
 * Каркас кабинета — копия supply. Приложение занимает ровно экран, а
 * прокручивается только рабочая область.
 */
export function AppLayout({ children }: { children: ReactNode }) {
  const { data: user, isPending } = useIdentity();
  const { mutate: logout } = useLogout();
  const { pathname } = useLocation();
  const personal = pathname === "/personal";
  const [mobileMenu, setMobileMenu] = useState(false);
  // Пункт без права не рисуется, группа без пунктов — тоже.
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter((i) => !i.need || has(user, i.need)) })).filter(
    (g) => g.items.length > 0,
  );
  // Заголовок — из ПОЛНОГО списка: человек без права стоит на экране по прямой
  // ссылке (экран сам пишет «Нет доступа»), и заголовок обязан его назвать.
  const current = NAV.flatMap((g) => g.items).find((i) => i.to === pathname);
  const [theme, setTheme] = useState<"dark" | "light">(
    () => (document.documentElement.dataset["theme"] as "dark" | "light") ?? "dark",
  );

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.dataset["theme"] = next;
    setTheme(next);
  };

  return (
    <div className={`grid h-screen overflow-hidden ${personal ? "grid-cols-1 md:grid-cols-[var(--spacing-sidebar)_minmax(0,1fr)]" : "grid-cols-[var(--spacing-sidebar)_minmax(0,1fr)]"}`}>
      {personal && mobileMenu ? <button type="button" aria-label="Закрыть меню" onClick={() => setMobileMenu(false)} className="fixed inset-0 z-30 bg-bg/90 md:hidden" /> : null}
      <aside id="app-navigation" data-annot="меню" className={`h-screen flex-col border-r border-border bg-bg-subtle ${personal ? `${mobileMenu ? "flex" : "hidden"} fixed left-0 top-0 z-40 w-[var(--spacing-sidebar)] md:static md:flex md:w-auto` : "flex"}`}>
        <div className="flex h-[var(--spacing-topbar)] flex-none items-center gap-2 border-b border-border px-3">
          <div className="grid size-[22px] flex-none place-items-center rounded-[5px] bg-fg text-[12px] font-semibold text-bg">
            F
          </div>
          <div className="min-w-0 flex-1 text-[14px] font-semibold tracking-[-0.015em]">finance</div>
          {personal ? <button type="button" aria-label="Закрыть навигацию" onClick={() => setMobileMenu(false)} className="rounded p-1 hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-accent md:hidden"><X size={16} /></button> : null}
          {/* Контур CRM, по которому сверяется вход: dev-вход не должен
              выглядеть боевым. */}
          <span
            title={user?.crm ? `вход сверяется с CRM: ${user.crm}` : "контур CRM неизвестен"}
            className={`flex-none rounded border px-1.5 font-mono text-[10px] ${
              user?.crm_is_prod === false ? "border-warn text-warn" : "border-border text-fg-faint"
            }`}
          >
            {user?.crm_is_prod === false ? "dev" : "prod"}
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          {nav.map((group) => (
            <div key={group.group} className="mb-4">
              <div className="px-2 pb-1.5 text-[10.5px] font-medium uppercase tracking-[0.06em] text-fg-faint">
                {group.group}
              </div>
              {group.items.map((item) => {
                const active = item.to === pathname;
                const cls =
                  "flex h-8 w-full items-center gap-2.5 rounded-[var(--radius-ui)] px-2 text-[13.5px] transition-colors " +
                  (active ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:bg-bg-hover hover:text-fg");
                return (
                  <Link key={item.to} to={item.to} aria-current={active ? "page" : undefined} className={cls} onClick={() => setMobileMenu(false)}>
                    {item.icon}
                    {item.label}
                  </Link>
                );
              })}
            </div>
          ))}
          {user && nav.length === 0 ? (
            <div className="px-2 text-[12px] text-fg-faint">
              Разделов пока нет: права выдаёт тот, кто управляет полномочиями.
            </div>
          ) : null}
        </nav>

        <div className="grid flex-none gap-2 border-t border-border p-2">
          <div className="flex items-center gap-2 rounded-[var(--radius-ui)] px-1.5 py-1">
            <div className="size-[22px] flex-none rounded-full bg-accent" />
            <div className="min-w-0">
              <div className="truncate text-[12.5px]">{user?.name ?? "…"}</div>
              <div className="text-[11px] text-fg-faint">финансы</div>
            </div>
            <button
              type="button"
              onClick={() => logout()}
              title="Выйти"
              className="ml-auto rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-fg"
            >
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* Имя экрана — начало адреса в разметке: «Операции › операция 812»,
          а не безымянная строка. */}
      <div data-annot={current?.label ?? "экран"} className="flex min-h-0 min-w-0 flex-col">
        <header data-annot="шапка" className="sticky top-0 z-30 flex h-[var(--spacing-topbar)] items-center gap-3 border-b border-border bg-bg/85 px-5 backdrop-blur">
          {personal ? <button type="button" aria-label="Открыть меню" aria-controls="app-navigation" aria-expanded={mobileMenu} onClick={() => setMobileMenu((was) => !was)} className="rounded p-1 hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-accent md:hidden"><Menu size={18} /></button> : null}
          <div className="flex min-w-0 items-center gap-2 text-[13.5px] text-fg-muted">
            <span className={personal ? "hidden sm:inline" : ""}>uni-renter</span>
            <ChevronRight size={13} className={`text-border-strong ${personal ? "hidden sm:inline" : ""}`} />
            <span className={personal ? "hidden sm:inline" : ""}>finance</span>
            <ChevronRight size={13} className={`text-border-strong ${personal ? "hidden sm:inline" : ""}`} />
            <span className="font-medium text-fg">{current?.label ?? "Кабинет"}</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              title="Тема"
              className="grid h-8 w-8 place-items-center rounded-[var(--radius-ui)] border border-border hover:bg-bg-hover"
            >
              <Moon size={14} />
            </button>
          </div>
        </header>

        {/* Экран — после личности: права едут в ней. `null` — ответ 401. */}
        {user ? (
          children
        ) : user === null ? (
          <Navigate to="/login" replace />
        ) : (
          <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 text-[12.5px] text-fg-muted">
            {isPending ? "…" : "Не удалось узнать, кто вошёл, — обновите страницу."}
          </main>
        )}
      </div>

      {/* Разметка живёт в каркасе, а не на экране: комментировать можно любой
          экран кабинета (копия supply). */}
      <Annotator />
    </div>
  );
}
